/**
 * useDebouncedAutoRecalc — Phase 1 Mockup→Live-Integration
 *
 * Löst 500 ms nach der letzten Zeilen-Änderung einen deterministischen
 * Recalc gegen das Backend aus. Ersetzt (bei aktivem Feature-Flag
 * step3AutoRecalc) den manuellen „Summe aktualisieren"-Button.
 *
 * Design-Detail (wichtig!):
 *   `runRecalc` ist per Ref an den Hook gebunden, NICHT via useCallback-Prop
 *   in den Effect-Dependencies. Grund: der Consumer erzeugt runRecalc
 *   typischerweise bei jedem Render neu (Closure über den State). Wäre
 *   runRecalc eine Effect-Dep, würde der Effect bei jedem Re-Render den
 *   Timer cancellen und neu aufsetzen — der Debounce würde NIE ablaufen.
 *
 * Race-Protection:
 *   - AbortController bricht laufende Requests bei neuen Triggern ab
 *   - Sequenz-Nummer verwirft Antworten von veralteten Requests
 */

import { useEffect, useRef, useState } from "react";

/**
 * @param {object} opts
 * @param {boolean} opts.enabled Feature aktiv?
 * @param {any}     opts.trigger Value dessen Änderung den Debounce startet
 *                               (typischerweise `rows`).
 * @param {boolean} opts.isDirty Externes „muss recalced werden"-Signal.
 * @param {boolean} opts.paused  Kein Recalc jetzt (z.B. laufender Extract).
 * @param {(seq:number, signal:AbortSignal) => Promise<void>} opts.runRecalc
 * @param {number} [opts.delay=500] Debounce in ms.
 * @returns {{ status: "idle"|"pending"|"running" }}
 */
export function useDebouncedAutoRecalc({
  enabled,
  trigger,
  isDirty,
  paused,
  runRecalc,
  delay = 500,
}) {
  const [status, setStatus] = useState("idle");
  // runRecalc als Ref → keine dep, immer aktuell
  const runRecalcRef = useRef(runRecalc);
  useEffect(() => {
    runRecalcRef.current = runRecalc;
  }, [runRecalc]);

  const abortRef = useRef(null);
  const seqRef = useRef(0);
  const firstRunRef = useRef(true);

  useEffect(() => {
    // Ersten Trigger (Mount) überspringen — der ist immer der initiale
    // Extract-Result und darf kein zweites Recalc auslösen.
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return undefined;
    }
    if (!enabled) return undefined;
    if (paused) return undefined;
    if (!isDirty) return undefined;

    setStatus("pending");
    const timer = setTimeout(async () => {
      // Vorherigen Request abbrechen falls einer läuft
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_e) { /* noop */ }
      }
      const controller = new AbortController();
      abortRef.current = controller;
      seqRef.current += 1;
      const mySeq = seqRef.current;
      setStatus("running");
      try {
        await runRecalcRef.current(mySeq, controller.signal);
      } catch (_err) {
        /* Fehler-Handling delegiert an runRecalc-Consumer */
      } finally {
        if (seqRef.current === mySeq) {
          setStatus("idle");
          abortRef.current = null;
        }
      }
    }, delay);

    return () => {
      clearTimeout(timer);
      // Status nur zurück auf idle wenn wir wirklich noch "pending" sind
      // (nicht wenn Timer bereits ausgeführt hat → dann läuft der Recalc)
      setStatus((prev) => (prev === "pending" ? "idle" : prev));
    };
    // trigger + Kern-Steuer-Werte, KEIN runRecalc (per Ref abgehandelt)
  }, [trigger, enabled, paused, isDirty, delay]);

  // Iter 20.36.f9.1 — Watchdog-Fallback gegen Race-Condition beim
  // Rapid-Klicken. User-Report: „mehrfaches Klicken auf ANR-Checkbox
  // → nur pulsierendes Icon, es kommt keine neue Summe".
  //
  // Ursache: Beim Rapid-Klicken interagieren Timer-Abbrüche, Abort-
  // Controller, Generations-Counter und React-Batching so subtil,
  // dass der Haupt-Effect in Randfällen keinen neuen Timer schedulet,
  // obwohl `isDirty=true, paused=false, enabled=true` ist. Der Effect
  // feuert nur bei Dep-Änderungen — wenn diese aber im selben React-
  // Batch verrechnet werden (paused schwingt true→false→true), kann
  // ein Timer verpasst werden.
  //
  // Watchdog: Wenn wir uns in einem „sollte laufen, tut aber nicht"-
  // Zustand befinden (idle + dirty + not paused + enabled), starten
  // wir einen kurzen Fallback-Timer, der den Recalc-Trigger nachholt.
  // Der Fallback nutzt bewusst nur `trigger`-unabhängige State-Werte,
  // damit er auch bei fehlenden Trigger-Wechseln greift.
  useEffect(() => {
    if (!enabled || paused || !isDirty) return undefined;
    if (status !== "idle") return undefined;
    const watchdog = setTimeout(async () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_e) { /* noop */ }
      }
      const controller = new AbortController();
      abortRef.current = controller;
      seqRef.current += 1;
      const mySeq = seqRef.current;
      setStatus("running");
      try {
        await runRecalcRef.current(mySeq, controller.signal);
      } catch (_err) {
        /* delegiert an consumer */
      } finally {
        if (seqRef.current === mySeq) {
          setStatus("idle");
          abortRef.current = null;
        }
      }
    }, delay);
    return () => clearTimeout(watchdog);
  }, [enabled, paused, isDirty, status, delay]);

  // Beim Unmount alles abbrechen
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_e) { /* noop */ }
      }
    };
  }, []);

  return { status };
}
